import {FormField} from '../../../types/form-builder.types';
import {useMemo, useRef} from 'react';

interface MatrixFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

export function MatrixField({ field, value, onChange }: MatrixFieldProps) {
  const config = field.config;
  const rows = Array.isArray(config.rows) ? config.rows : [];
  const columns = Array.isArray(config.columns) ? config.columns : [];
  const allowMultiple = config.allowMultiple || false;
  const matrixValue = value || {};

  // Store randomization seed in ref to prevent re-randomization
  const randomSeedRef = useRef<number | null>(null);

  // Randomize rows ONCE on mount if configured
  const displayRows = useMemo(() => {
    if (!config.randomizeRows || rows.length === 0) {
      return rows;
    }

    // Generate seed once and store it
    if (randomSeedRef.current === null) {
      randomSeedRef.current = Math.random();
    }

    // Create a copy and shuffle using Fisher-Yates algorithm
    const shuffled = [...rows];
    // Use the stored seed for consistent randomization
    let seed = randomSeedRef.current;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }, [rows, config.randomizeRows, randomSeedRef.current]); // Stable dependencies

  const handleCellChange = (rowValue: string, colValue: string, checked?: boolean) => {
    const newValue = { ...matrixValue };
    
    if (allowMultiple) {
      // Multiple selection per row
      if (!newValue[rowValue]) newValue[rowValue] = [];
      if (checked) {
        newValue[rowValue] = [...newValue[rowValue], colValue];
      } else {
        newValue[rowValue] = newValue[rowValue].filter((v: string) => v !== colValue);
      }
    } else {
      // Single selection per row
      newValue[rowValue] = colValue;
    }
    
    onChange(newValue);
  };

  return (
    <div className="w-full">
      {/* Responsive table wrapper */}
      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: '500px', borderSpacing: 0 }}>
          {/* Header */}
          <thead>
            <tr>
              <th
                className="text-left font-medium text-sm py-2 pr-6"
                style={{ color: 'var(--ef-text, #1E293B)' }}
              ></th>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className="text-center font-medium text-sm py-2 px-3"
                  style={{ color: 'var(--ef-text, #1E293B)' }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
            <tr>
              <th colSpan={columns.length + 1} style={{ padding: 0 }}>
                <div style={{
                  height: '1px',
                  background: 'var(--ef-border, #E2E8F0)',
                  margin: '8px 0'
                }} />
              </th>
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {displayRows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className="group transition-colors"
                style={{
                  background: config.alternateRowColors && rowIndex % 2 === 1
                    ? 'var(--ef-surface, #FAFBFC)'
                    : 'transparent'
                }}
              >
                {/* Row label */}
                <td
                  className="text-sm py-3 pr-6 align-middle"
                  style={{ color: 'var(--ef-text, #1E293B)' }}
                >
                  {row.label}
                </td>

                {/* Radio buttons */}
                {columns.map((col) => {
                  const isSelected = allowMultiple
                    ? matrixValue[row.value]?.includes(col.value)
                    : matrixValue[row.value] === col.value;

                  return (
                    <td key={col.id} className="text-center py-3 px-3 align-middle">
                      <label className="inline-flex cursor-pointer">
                        <input
                          type={allowMultiple ? "checkbox" : "radio"}
                          name={allowMultiple ? undefined : `matrix-${field.id}-${row.value}`}
                          value={col.value}
                          checked={isSelected}
                          onChange={(e) =>
                            handleCellChange(row.value, col.value, allowMultiple ? e.target.checked : undefined)
                          }
                          className="peer sr-only"
                        />
                        <span
                          className="inline-flex items-center justify-center rounded-full border transition-all"
                          style={{
                            width: '20px',
                            height: '20px',
                            borderColor: isSelected
                              ? 'var(--ef-primary, #3B82F6)'
                              : 'var(--ef-border, #D1D5DB)',
                            borderWidth: '1.5px',
                            backgroundColor: isSelected
                              ? 'var(--ef-primary, #3B82F6)'
                              : 'transparent',
                            flexShrink: 0
                          }}
                        >
                          {/* Inner white dot - always rendered but invisible when not selected to maintain layout */}
                          <span
                            className="rounded-full transition-opacity"
                            style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: 'white',
                              opacity: isSelected ? 1 : 0
                            }}
                          />
                        </span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
